import { useEffect, useRef, useState } from 'react';
import { Loader, Group, Table } from '@mantine/core';
import Header from '../../../components/util/header';
import { useSelector } from 'react-redux';
import { IAPMTransaction } from '../../../../libraries/apm';
import { AppState } from '../../../client-redux/reducers';
import { Language } from '../../../languages/languageHandler';
import PaginationComponent from '../../../components/pagination';
import { LoginState } from '../../../client-redux/login/reducer';
import APMReceipt from '../../../components/apm-receipt';
import FilterBar, { FilterBarFieldsData } from '../../../components/filterbar/index';
import { getLocationName } from '../../../utils/script';
import { fetchAPMTransactionList, fetchAPMTransactionsXLS } from '../../../../libraries/apm';
import { useFitTableRows } from '../../../components/util/fit-table-rows-hook';
import { formatDateTimeLabel, formatDateToISO } from '../../../utils/date';
import { useStatusOperDialog, isZarphSuccessResponse } from '../../../context/status-oper-dialog';
import './styles.scss';

const APMTransactions = () => {
  const lang = useSelector<AppState, Language>(state => state.lang.lang);
  const user = useSelector<AppState, LoginState>(state => state.login);
  const region = useSelector<AppState, string>(state => state.login.user?.region || 'UTC');
  const controller = useRef(new AbortController()).current;

  const [loading, setLoading] = useState(false);
  const [totalRows, setTotalRows] = useState<number>(0);
  const [offset, setOffset] = useState<number>(0);
  const {
    limit = 10,
    setLimit,
    tableRef
  } = useFitTableRows({
    bufferRows: 0,
    minHeightForDynamic: 400
  });
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [list, setList] = useState<IAPMTransaction[]>([]);

  const [filterBarData, setFilterBarData] = useState<FilterBarFieldsData>();
  const { handleApiError } = useStatusOperDialog();

  useEffect(() => {
    if (filterBarData && limit) {
      loadAPMTransactionList();
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterBarData, limit, offset]);

  const handleFilterBarOnDataChange = (data: FilterBarFieldsData) => {
    setFilterBarData(data);
    changePage(1);
  };

  const loadAPMTransactionList = () => {
    if (!filterBarData || !limit) {
      return;
    }

    setLoading(true);
    setList([]);
    setTotalRows(0);

    fetchAPMTransactionList(
      controller,
      filterBarData.entity || user.user?.entid || 0,
      filterBarData.lid || 0,
      filterBarData.posid || 0,
      offset,
      limit,
      true,
      formatDateToISO(filterBarData.beginDateTime),
      formatDateToISO(filterBarData.endDateTime)
    )
      .then(response => {
        if (isZarphSuccessResponse(response, [9004])) {
          setList(response?.data?.transactions || []);
          setTotalRows(response?.data?.totalRows || 0);
        } else {
          console.error('Error response:', response);
          handleApiError(response, lang.ALERT_ERROR_FETCHING_DATA);
          setLoading(false);
        }
      })
      .catch(err => {
        // unexpected errors go here
        handleApiError(err, lang.ALERT_ERROR_FETCHING_DATA);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const changePage = (page: number) => {
    const newOffset = (page - 1) * limit;
    setCurrentPage(page);
    setOffset(newOffset);
  };

  const resultsSelect = (newValue: number) => {
    const newLimit = Number(newValue);
    setLimit(newLimit);
    setCurrentPage(1);
    setOffset(0);
  };

  const getXLSReport = () => {
    if (!filterBarData) return;
    return fetchAPMTransactionsXLS(
      controller,
      filterBarData.entity || 0,
      filterBarData.lid || 0,
      offset,
      limit,
      formatDateToISO(filterBarData.beginDateTime),
      formatDateToISO(filterBarData.endDateTime)
    );
  };

  return (
    <div className="APMtransactions">
      <Header pageTitle={lang.APM_TRANSACTIONS} />
      <FilterBar
        onSearch={loadAPMTransactionList}
        onDataChange={handleFilterBarOnDataChange}
        fieldsUsage={{
          entity: true,
          lid: true,
          posid: true,
          beginDateTime: true,
          endDateTime: true
        }}
        xlsReport={list.length ? getXLSReport : undefined}
      />

      <Table.ScrollContainer minWidth={500} ref={tableRef}>
        <Table striped verticalSpacing="sm">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>POS</Table.Th>
              <Table.Th>{lang.EQUIPMENT_LOCAL}</Table.Th>
              <Table.Th>{lang.GLOBAL_PERIOD}</Table.Th>
              <Table.Th>{lang.TRANSACTION_NUMBER}</Table.Th>
              <Table.Th>{lang.GLOBAL_DATE_TIME}</Table.Th>
              <Table.Th>{lang.GLOBAL_AMOUNT}</Table.Th>
              <Table.Th></Table.Th>
            </Table.Tr>
          </Table.Thead>

          <Table.Tbody>
            {loading ? (
              <Table.Tr>
                <Table.Td colSpan={12}>
                  <Loader color="#016273" />
                </Table.Td>
              </Table.Tr>
            ) : (
              <>
                {!list.length ? (
                  <Table.Tr>
                    <Table.Td colSpan={12} style={{ textAlign: 'center' }}>
                      {lang.GLOBAL_NO_RESULTS}
                    </Table.Td>
                  </Table.Tr>
                ) : (
                  list?.map((transaction, index) => {
                    return (
                      <Table.Tr key={index}>
                        <Table.Td>{transaction.key.a001}</Table.Td>
                        <Table.Td>{getLocationName(transaction.sid)}</Table.Td>
                        <Table.Td>{transaction.key.a031}</Table.Td>
                        <Table.Td>{transaction.key.a032}</Table.Td>
                        <Table.Td>{formatDateTimeLabel(transaction.dateTime, region)}</Table.Td>
                        <Table.Td>{(transaction.amount / 100).toFixed(2) + ' €'}</Table.Td>

                        <Table.Td>
                          <APMReceipt
                            a001={transaction.key.a001}
                            movType={transaction.key.movType}
                            a031={transaction.key.a031}
                            a032={transaction.key.a032}
                            a029={transaction.key.a029}
                            dateTime={transaction.key.dateTime}
                          />
                        </Table.Td>
                      </Table.Tr>
                    );
                  })
                )}
              </>
            )}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>

      <Group>
        <PaginationComponent
          total={totalRows}
          itemsPerPage={limit}
          dynamicLimit={limit}
          currentPage={currentPage}
          onPageChange={changePage}
          onResultsChange={resultsSelect}
          offset={offset}
        />
      </Group>
    </div>
  );
};

export default APMTransactions;
